import { usePermissao } from "@/utils/permissao";
import { useMarca } from "@/components/providers/MarcaProvider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FichaService, SinistroService } from "@/services";
import { getMesLabel } from "@/types/enum/Mes";
import { getTipoSeguroLabel } from "@/types/enum/TipoSeguro";
import { formatDate } from "@/utils/DateUtils";
import { getLinkFichasDoMes } from "@/utils/FichaUtils";
import { getTenant } from "@/utils/TenantUtils";
import { cn } from "@/utils/cn";
import { AlertTriangle, CalendarClock, Plus, ShieldCheck, TriangleAlert } from "lucide-react";
import { IconeTile, TIPO_SEGURO_CORES } from "@/components/ui/cores";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type Mes = { mes: number; ano: number; quantidade: number };
type Fatia = { nome: string; quantidade: number; link?: string; cor?: string };

const numero = new Intl.NumberFormat('pt-BR');
const PALETA = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-sky-500'];
const isoDate = (d: Date) => formatDate(d);

// Mês anterior + atual + 3 seguintes, preenchendo com zero os meses sem vencimentos
const janelaDeMeses = (hoje: Date): Mes[] =>
  [-1, 0, 1, 2, 3].map(offset => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() + offset, 1);
    return { mes: d.getMonth() + 1, ano: d.getFullYear(), quantidade: 0 };
  });

const useContagem = (fetch: () => Promise<{ data: number }>) => {
  const [valor, setValor] = useState<number>();
  useEffect(() => { fetch().then(r => setValor(r.data)).catch(() => setValor(0)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return valor;
};

// Sem acesso aos seguros o painel (todo feito de seguros e sinistros) dá lugar a uma saudação
export const DashboardPage = () => {
  const marca = useMarca();
  if (!usePermissao('SEGURO_READ')) {
    return (
      <div className="pt-2">
        <h1 className="text-2xl font-semibold tracking-tight">Bem-vindo ao {marca.titulo}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Use o menu ao lado para acessar as áreas liberadas para você.</p>
      </div>
    );
  }
  return <PainelSeguros />;
}

const PainelSeguros = () => {
  const navigate = useNavigate();
  const podeAlterar = usePermissao('SEGURO_WRITE');
  const tenant = getTenant();
  const hoje = new Date();
  const em30Dias = new Date();
  em30Dias.setDate(hoje.getDate() + 30);

  const vigentes = useContagem(() => FichaService.contarVigentes());
  const vencendo = useContagem(() => FichaService.contar(`vigenciaFinal=ge="${isoDate(hoje)}";vigenciaFinal=le="${isoDate(em30Dias)}"`));
  const sinistros = useContagem(() => SinistroService.contarPendentes());

  const [meses, setMeses] = useState<Mes[]>();
  const [seguradoras, setSeguradoras] = useState<Fatia[]>();
  const [tipos, setTipos] = useState<Fatia[]>();

  useEffect(() => {
    const janela = janelaDeMeses(hoje);
    const [ini, fim] = [janela[0], janela[janela.length - 1]];
    FichaService.findGroupByPeriod(ini.mes, ini.ano, fim.mes, fim.ano)
      .then(r => setMeses(janela.map(m => ({ ...m, quantidade: r.data?.find((d: Mes) => d.mes === m.mes && d.ano === m.ano)?.quantidade ?? 0 }))))
      .catch(() => setMeses(janela));

    // Top 5 seguradoras; as demais somadas em "Outras" (sem link)
    FichaService.findChartPorSeguradora()
      .then(r => {
        const ordenadas = [...r.data].sort((a: any, b: any) => b.quantidade - a.quantidade);
        const top: Fatia[] = ordenadas.slice(0, 5).map((d: any, i: number) => ({
          nome: d.nomeSeguradora,
          quantidade: d.quantidade,
          cor: PALETA[i % PALETA.length],
          link: `/${tenant}/seguros?filters==Seguradora_Contém=${d.nomeSeguradora};Vigência Inicial_Menor ou igual=${formatDate(hoje)};Vigência Final_Maior ou igual=${formatDate(hoje)}`
        }));
        const outras = ordenadas.slice(5).reduce((s: number, d: any) => s + d.quantidade, 0);
        setSeguradoras(outras ? [...top, { nome: 'Outras', quantidade: outras, cor: 'bg-muted-foreground/40' }] : top);
      })
      .catch(() => setSeguradoras([]));

    FichaService.findSegurosVigentesAgrupadoPorTipo()
      .then(r => setTipos(r.data.map((d: any) => ({ nome: getTipoSeguroLabel(d.tipoSeguro) || d.tipoSeguro, quantidade: d.quantidade, cor: TIPO_SEGURO_CORES[d.tipoSeguro]?.barra }))))
      .catch(() => setTipos([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const data = hoje.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  const dataExtenso = data.charAt(0).toUpperCase() + data.slice(1);

  return (
    <div className="space-y-6 pb-10">
      <header className="flex flex-wrap items-end justify-between gap-4 pt-2">
        <div>
          <p className="text-sm text-muted-foreground">{dataExtenso}</p>
          <h1 className="text-2xl font-semibold tracking-tight">Sua carteira hoje</h1>
        </div>
        {podeAlterar && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/${tenant}/sinistro/novo`)}>
              <AlertTriangle className="mr-2 h-4 w-4" /> Abrir sinistro
            </Button>
            <Button onClick={() => navigate(`/${tenant}/seguros/novo`)}>
              <Plus className="mr-2 h-4 w-4" /> Novo seguro
            </Button>
          </div>
        )}
      </header>

      <section aria-label="Indicadores" className="grid overflow-hidden rounded-lg border bg-card sm:grid-cols-3 sm:divide-x max-sm:divide-y">
        <Indicador
          valor={vigentes}
          rotulo="Seguros vigentes"
          icon={<ShieldCheck />}
          cor="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          onClick={() => navigate(`/${tenant}/seguros?filters==Vigência Inicial_Menor ou igual=${formatDate(hoje)};Vigência Final_Maior ou igual=${formatDate(hoje)}`)}
        />
        <Indicador
          valor={vencendo}
          rotulo="Vencem nos próximos 30 dias"
          icon={<CalendarClock />}
          cor="bg-amber-500/10 text-amber-600 dark:text-amber-400"
          destaque={!!vencendo}
          onClick={() => navigate(`/${tenant}/seguros?filters==Vigência Final_Maior ou igual=${formatDate(hoje)};Vigência Final_Menor ou igual=${formatDate(em30Dias)}`)}
        />
        <Indicador
          valor={sinistros}
          rotulo="Sinistros pendentes"
          icon={<TriangleAlert />}
          cor="bg-rose-500/10 text-rose-600 dark:text-rose-400"
          destaque={!!sinistros}
          onClick={() => navigate(`/${tenant}/sinistro?filters==status_Igual=PENDENTE`)}
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-5">
        <Painel titulo="Renovações" descricao="Seguros com vigência terminando em cada mês" className="lg:col-span-3">
          <Renovacoes meses={meses} hoje={hoje} onSelect={m => navigate(getLinkFichasDoMes(m.mes, m.ano))} />
        </Painel>

        <div className="space-y-6 lg:col-span-2">
          <Painel titulo="Seguros por seguradora">
            <Ranking itens={seguradoras} vazio="Nenhum seguro vigente no momento." onSelect={i => navigate(i.link!)} />
          </Painel>
          <Painel titulo="Seguros por tipo">
            <Ranking itens={tipos} vazio="Nenhum seguro vigente no momento." />
          </Painel>
        </div>
      </div>
    </div>
  );
};

const Indicador = ({ valor, rotulo, destaque, icon, cor, onClick }: { valor?: number; rotulo: string; destaque?: boolean; icon: ReactNode; cor: string; onClick(): void }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={valor === undefined}
    className="group flex items-center gap-4 px-6 py-5 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
  >
    <IconeTile cor={cor}>{icon}</IconeTile>
    <span className="flex flex-col gap-1">
      {valor === undefined
        ? <Skeleton className="h-10 w-16" />
        : <span className={cn("text-4xl font-light tabular-nums tracking-tight", destaque && "text-amber-700 dark:text-amber-400")}>{numero.format(valor)}</span>}
      <span className="text-sm text-muted-foreground group-hover:text-foreground">{rotulo}</span>
    </span>
  </button>
);

const Painel = ({ titulo, descricao, className, children }: { titulo: string; descricao?: string; className?: string; children: ReactNode }) => (
  <section className={cn("rounded-lg border bg-card p-6", className)}>
    <h2 className="font-semibold">{titulo}</h2>
    {descricao && <p className="text-sm text-muted-foreground">{descricao}</p>}
    <div className="mt-5">{children}</div>
  </section>
);

const Renovacoes = ({ meses, hoje, onSelect }: { meses?: Mes[]; hoje: Date; onSelect(m: Mes): void }) => {
  if (!meses) return <Skeleton className="h-56 w-full" />;
  const max = Math.max(1, ...meses.map(m => m.quantidade));
  const vazio = meses.every(m => !m.quantidade);

  return (
    <div className="relative">
      {vazio && (
        <p className="absolute inset-x-0 top-10 text-center text-sm text-muted-foreground">
          Nenhum seguro vence entre {getMesLabel(meses[0].mes).toLowerCase()} e {getMesLabel(meses[meses.length - 1].mes).toLowerCase()}.
        </p>
      )}
      <ol className="grid h-56 grid-cols-5 gap-3">
        {meses.map((m, idx) => {
          const atual = m.mes === hoje.getMonth() + 1 && m.ano === hoje.getFullYear();
          const passado = new Date(m.ano, m.mes - 1) < new Date(hoje.getFullYear(), hoje.getMonth());
          return (
            <li key={`${m.ano}-${m.mes}`}>
              <button
                type="button"
                onClick={() => onSelect(m)}
                aria-label={`${getMesLabel(m.mes)} de ${m.ano}: ${m.quantidade} seguros vencendo`}
                className="group flex h-full w-full flex-col justify-end rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className={cn("mb-1 text-2xl font-light tabular-nums", passado && "text-muted-foreground", atual && "font-normal text-primary")}>
                  {m.quantidade}
                </span>
                <span
                  className={cn(
                    "w-full origin-bottom rounded-sm transition-opacity group-hover:opacity-80 motion-safe:animate-grow-y",
                    atual ? "bg-primary" : passado ? "bg-muted" : "bg-primary/25"
                  )}
                  style={{ height: `${Math.max(4, (m.quantidade / max) * 100)}%`, maxHeight: '9rem', animationDelay: `${idx * 80}ms` }}
                />
                <span className={cn("mt-2 border-t pt-2 text-left text-sm", atual ? "border-primary font-medium text-foreground" : "text-muted-foreground")}>
                  {getMesLabel(m.mes).slice(0, 3)}
                  {m.ano !== hoje.getFullYear() && <span className="text-xs"> {m.ano}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const Ranking = ({ itens, vazio, onSelect }: { itens?: Fatia[]; vazio: string; onSelect?(i: Fatia): void }) => {
  if (!itens) return <div className="space-y-3"><Skeleton className="h-5 w-full" /><Skeleton className="h-5 w-2/3" /></div>;
  if (!itens.length) return <p className="text-sm text-muted-foreground">{vazio}</p>;

  // "Outras" (agrupamento, sem link) fica sempre por último
  const ordenados = [...itens].sort((a, b) => Number(a.nome === 'Outras') - Number(b.nome === 'Outras') || b.quantidade - a.quantidade);
  const total = ordenados.reduce((s, i) => s + i.quantidade, 0);

  return (
    <ul className="space-y-3">
      {ordenados.map((i, idx) => {
        const pct = total ? Math.round((i.quantidade / total) * 100) : 0;
        const conteudo = (
          <>
            <span className="flex justify-between gap-3 text-sm">
              <span className="truncate">{i.nome}</span>
              <span className="tabular-nums text-muted-foreground">{numero.format(i.quantidade)} <span className="text-xs">· {pct}%</span></span>
            </span>
            <span className="mt-1.5 block h-1.5 rounded-full bg-muted">
              <span className={cn("block h-full origin-left rounded-full motion-safe:animate-grow-x", i.cor || "bg-primary")} style={{ width: `${pct}%`, animationDelay: `${idx * 80}ms` }} />
            </span>
          </>
        );
        return (
          <li key={i.nome}>
            {onSelect && i.link
              ? <button type="button" onClick={() => onSelect(i)} className="block w-full rounded-sm text-left hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{conteudo}</button>
              : conteudo}
          </li>
        );
      })}
    </ul>
  );
};
